import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import socketService from "../config/ws-config";

/**
 * Custom hook for managing socket connections
 *
 * @example
 * // Basic usage - join room and subscribe
 * const { isConnected, sendMessage } = useSocket({
 *   rooms: storeId,
 *   subscriptions: {
 *     "store-update": (data) => console.log("Update:", data),
 *   },
 * });
 *
 * @example
 * // Multiple rooms
 * useSocket({
 *   rooms: [storeId, userId],
 *   subscriptions: { "topic": callback },
 * });
 *
 * @example
 * // Manual room management
 * const { joinRoom, leaveRoom } = useSocket({ autoJoin: false });
 *
 * @param {Object} options - Configuration options
 * @param {string|string[]} options.rooms - Room ID(s) to join. Can be a single string or array of strings
 * @param {Object} options.subscriptions - Object with topic as key and callback as value
 * @param {boolean} options.autoJoin - Whether to automatically join rooms (default: true)
 * @param {boolean} options.autoCleanup - Whether to automatically cleanup on unmount (default: true)
 * @param {Function} options.onReconnect - Called when the socket reconnects after a drop (receives attempt count)
 *
 * @returns Socket utilities
 * @returns {boolean} isConnected - Connection status
 * @returns {Function} joinRoom - Function to join a room
 * @returns {Function} leaveRoom - Function to leave a room
 * @returns {Function} sendMessage - Function to send a store message
 * @returns {Function} sendGeneralMessage - Function to send a general message
 * @returns {Function} subscribe - Function to subscribe to a topic
 * @returns {Function} unsubscribe - Function to unsubscribe from a topic
 */
export function useSocket({
  rooms = null,
  subscriptions = {},
  autoJoin = true,
  autoCleanup = true,
  onReconnect = null,
} = {}) {
  const subscriptionRefs = useRef(new Map());
  const roomsRef = useRef(new Set());
  const [isConnected, setIsConnected] = useState(() =>
    socketService.isConnected()
  );

  // Keep the latest onReconnect in a ref so we don't rebind the listener
  // every time the callback identity changes.
  const onReconnectRef = useRef(onReconnect);
  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  // Initialize socket connection and track connection status
  useEffect(() => {
    socketService.initialize();

    // Get socket instance and listen to connection events
    const socket = socketService.getSocket();
    if (!socket) {
      // If socket not ready, check again after a short delay
      const timer = setTimeout(() => {
        const retrySocket = socketService.getSocket();
        if (retrySocket) {
          setIsConnected(retrySocket.connected);
        }
      }, 100);
      return () => clearTimeout(timer);
    }

    // Update connection status on connect/disconnect events
    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);

    // Reconnect events live on the Manager (socket.io) in socket.io v4.
    const handleReconnect = (attempt) => {
      setIsConnected(true);
      const cb = onReconnectRef.current;
      if (typeof cb === "function") {
        cb(attempt);
      }
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.io.on("reconnect", handleReconnect);

    // Set initial state
    setIsConnected(socket.connected);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.io.off("reconnect", handleReconnect);
    };
  }, []);

  // Join rooms
  useEffect(() => {
    if (!autoJoin || !rooms) return;

    const roomsArray = Array.isArray(rooms) ? rooms : [rooms];

    roomsArray.forEach((roomId) => {
      if (roomId && !roomsRef.current.has(roomId)) {
        // Service handles duplicate joins with reference counting
        socketService.joinRoom(roomId);
        // Track that this hook instance "owns" this room join
        roomsRef.current.add(roomId);
      }
    });

    // Cleanup function
    if (autoCleanup) {
      return () => {
        roomsArray.forEach((roomId) => {
          if (roomId && roomsRef.current.has(roomId)) {
            // Only leave if this hook instance joined it
            // Check if other instances might be using it
            const globalRooms = socketService.getJoinedRooms();
            // For now, we'll leave - but in a production app you might want
            // reference counting to track multiple uses
            socketService.leaveRoom(roomId);
            roomsRef.current.delete(roomId);
          }
        });
      };
    }
  }, [rooms, autoJoin, autoCleanup]);

  // Store callbacks in refs to avoid re-subscribing when callback reference changes
  const callbacksRef = useRef(new Map());

  // Create a stable list of topic keys for dependency tracking
  const topicKeys = useMemo(() => {
    return Object.keys(subscriptions)
      .filter((key) => typeof subscriptions[key] === "function")
      .sort()
      .join(",");
  }, [subscriptions]);

  // Update callbacks ref when subscriptions change (this doesn't trigger re-subscription)
  useEffect(() => {
    // Update callbacks for all topics
    Object.entries(subscriptions).forEach(([topic, callback]) => {
      if (typeof callback === "function") {
        callbacksRef.current.set(topic, callback);
      } else {
        callbacksRef.current.delete(topic);
      }
    });
  }, [subscriptions]);

  // Handle subscriptions - only subscribe once per topic, use refs for callbacks
  useEffect(() => {
    const currentTopics = new Set(
      Object.keys(subscriptions).filter(
        (key) => typeof subscriptions[key] === "function"
      )
    );

    // Subscribe to new topics
    currentTopics.forEach((topic) => {
      // Only subscribe if we don't already have a subscription for this topic
      if (!subscriptionRefs.current.has(topic)) {
        // Create a stable wrapper callback that always calls the latest callback from ref
        const wrapperCallback = (data) => {
          const latestCallback = callbacksRef.current.get(topic);
          if (latestCallback && typeof latestCallback === "function") {
            latestCallback(data);
          }
        };

        const subscriptionId = socketService.subscribeToMessages(
          topic,
          wrapperCallback,
          `${topic}-${Date.now()}-${Math.random()}`
        );
        subscriptionRefs.current.set(topic, subscriptionId);
      }
    });

    // Cleanup subscriptions that are no longer in the subscriptions object
    subscriptionRefs.current.forEach((subscriptionId, topic) => {
      if (!currentTopics.has(topic)) {
        socketService.unsubscribeFromMessages(topic, subscriptionId);
        subscriptionRefs.current.delete(topic);
      }
    });

    // Cleanup subscriptions on unmount
    if (autoCleanup) {
      return () => {
        subscriptionRefs.current.forEach((subscriptionId, topic) => {
          socketService.unsubscribeFromMessages(topic, subscriptionId);
        });
        subscriptionRefs.current.clear();
        callbacksRef.current.clear();
      };
    }
  }, [topicKeys, autoCleanup]); // Only re-run when topic keys change, not callbacks

  // Manual room management functions
  const joinRoom = useCallback((roomId) => {
    if (roomId && !roomsRef.current.has(roomId)) {
      // Service handles duplicate joins with reference counting
      socketService.joinRoom(roomId);
      roomsRef.current.add(roomId);
    }
  }, []);

  const leaveRoom = useCallback((roomId) => {
    if (roomId && roomsRef.current.has(roomId)) {
      socketService.leaveRoom(roomId);
      roomsRef.current.delete(roomId);
    }
  }, []);

  // Message sending functions
  const sendMessage = useCallback((topic, message, storeId = null) => {
    socketService.sendMessage(topic, message, storeId);
  }, []);

  const sendGeneralMessage = useCallback((topic, message) => {
    socketService.sendGeneralMessage(topic, message);
  }, []);

  // Manual subscription management
  const subscribe = useCallback((topic, callback) => {
    const subscriptionId = socketService.subscribeToMessages(
      topic,
      callback,
      `${topic}-${Date.now()}-${Math.random()}`
    );
    subscriptionRefs.current.set(topic, subscriptionId);
    return subscriptionId;
  }, []);

  const unsubscribe = useCallback((topic, subscriptionId = null) => {
    socketService.unsubscribeFromMessages(topic, subscriptionId);
    if (subscriptionId) {
      subscriptionRefs.current.delete(topic);
    }
  }, []);

  return {
    isConnected,
    joinRoom,
    leaveRoom,
    sendMessage,
    sendGeneralMessage,
    subscribe,
    unsubscribe,
  };
}

/**
 * Hook that exposes the socket debug message log (sent + received).
 * Subscribes to log changes and re-renders when new messages arrive.
 *
 * @returns {Object}
 * @returns {Array} messages - The current message log (oldest first)
 * @returns {Function} clearMessages - Clear the log
 */
export function useSocketLog() {
  const [messages, setMessages] = useState(() =>
    socketService.getMessageLog()
  );

  useEffect(() => {
    socketService.initialize();
    // Sync current state, then subscribe to future changes
    setMessages([...socketService.getMessageLog()]);
    const unsubscribe = socketService.subscribeToLog((log) => {
      setMessages([...log]);
    });
    return unsubscribe;
  }, []);

  const clearMessages = useCallback(() => {
    socketService.clearMessageLog();
  }, []);

  return { messages, clearMessages };
}

/**
 * Hook that exposes the list of connected live screens (TVs).
 * Re-renders when a screen connects, disconnects, or times out.
 *
 * @returns {Array} connected screens (each with id, name, lastSeen, ...)
 */
export function useConnectedScreens() {
  const [screens, setScreens] = useState(() =>
    socketService.getConnectedScreens()
  );

  useEffect(() => {
    socketService.initialize();
    setScreens(socketService.getConnectedScreens());
    const unsubscribe = socketService.subscribeToScreens((list) => {
      setScreens([...list]);
    });
    return unsubscribe;
  }, []);

  return screens;
}

export default useSocket;
